import type { CommandDeckContinueResolution } from '../lib/resolveCommandDeckContinue'
import type { CommandDeckDeckPulseModel } from '../lib/commandDeckDeckPulse'
import type { CommandDeckWorkContextModel } from '../lib/commandDeckWorkContext'
import type { CommandDeckSurfaceMode } from '../contracts/types'
import { CommandDeckStatusPill } from './CommandDeckStatusPill'
import {
  continueAlignedPill,
  deckPulseModePill,
  libraryPendingPill,
  recallPlannedPill,
} from './commandDeckStatusPillPresentational'

interface Props {
  pulse: CommandDeckDeckPulseModel
  recall: CommandDeckWorkContextModel['recall']
  libraryAction: CommandDeckWorkContextModel['libraryAction']
  continueResolution: CommandDeckContinueResolution
  mode: CommandDeckSurfaceMode
  onPrimaryCta?: () => void
}

export function CommandDeckAmbientBand({
  pulse,
  recall,
  libraryAction,
  continueResolution,
  mode,
  onPrimaryCta,
}: Props) {
  const pulsePill = deckPulseModePill(mode)
  const alignedPill = continueAlignedPill(Boolean(continueResolution.label))
  const libraryPill = libraryPendingPill()
  const plannedPill = recallPlannedPill()

  return (
    <footer className="cd-mc-ambient cd-mc-ambient-band" aria-label="Cockpit ambient status and recall">
      <div className="cd-mc-ambient__pulse" role="status" aria-label="Deck pulse">
        <div className="cd-mc-ambient__pulse-head">
          <CommandDeckStatusPill label={pulsePill.label} variant={pulsePill.variant} />
          <span className="cd-mc-ambient__pulse-label">{pulse.statusLabel}</span>
        </div>
        <ul className="cd-mc-ambient__pulse-lines">
          {pulse.lines.map((line) => (
            <li key={line.text} data-source={line.source}>
              {line.text}
            </li>
          ))}
        </ul>
      </div>

      <div className="cd-mc-ambient__recall" aria-label="Pinned and favourites recall">
        <span className="cd-mc-ambient__recall-k">{recall.pinnedLabel}</span>
        <CommandDeckStatusPill label={plannedPill.label} variant={plannedPill.variant} />
        {recall.pinned.map((item) => (
          <span key={item.label} className="cd-mc-ambient__chip" data-source={item.source}>
            {item.label}
          </span>
        ))}
        <span className="cd-mc-ambient__sep" aria-hidden="true">
          ·
        </span>
        <span className="cd-mc-ambient__recall-k">{recall.favouritesLabel}</span>
        <CommandDeckStatusPill label={plannedPill.label} variant={plannedPill.variant} />
        {recall.favourites.map((item) => (
          <span key={item.label} className="cd-mc-ambient__chip" data-source={item.source}>
            {item.label}
          </span>
        ))}
        <CommandDeckStatusPill label={libraryPill.label} variant={libraryPill.variant} />
        <span className="cd-mc-ambient__library" data-source={libraryAction.source}>
          {libraryAction.detailLabel}
        </span>
        {alignedPill ? (
          <CommandDeckStatusPill label={alignedPill.label} variant={alignedPill.variant} />
        ) : null}
      </div>

      {continueResolution.label ? (
        <button
          type="button"
          className="command-deck-btn command-deck-btn--primary cd-mc-ambient__continue"
          onClick={onPrimaryCta}
          disabled={!continueResolution.navigable}
          aria-disabled={!continueResolution.navigable}
          title={
            continueResolution.navigable
              ? continueResolution.label
              : `${continueResolution.label} — advisory only`
          }
        >
          {continueResolution.label}
        </button>
      ) : null}
    </footer>
  )
}
